import useSWR from 'swr';
import { useAuth } from '../contexts/AuthContext';

interface BusinessSettings {
  id?: string;
  business_id?: string;
  tax_rate?: number;
  late_fee_mode?: 'flat' | 'percent' | 'off';
  late_fee_amount?: number;
  currency?: string;
  primary_color?: string;
  secondary_color?: string;
  accent_color?: string;
  font_family?: string;
  logo_path?: string;
  logo_width?: number | null;
  logo_height?: number | null;
  logo_layout?: 'wide' | 'standard' | 'tall' | null;
  use_html_pdf?: boolean;
  enable_invoice_balance_forward?: boolean;
  business_name_font_size?: number;
  body_font_size?: number;
  invoice_title_font_size?: number;
  section_title_font_size?: number;
  status_badge_font_size?: number;
  totals_final_font_size?: number;
  paid_banner_font_size?: number;
  table_header_font_size?: number;
  table_cell_font_size?: number;
  timezone?: string;
}

export function useBusinessSettings() {
  const { token, currentBusiness, isCookieMode, API_BASE_URL } = useAuth();

  const { data, error, mutate } = useSWR<BusinessSettings>(
    currentBusiness ? [`${API_BASE_URL}/businesses/${currentBusiness.id}/settings`] : null,
    async ([url]) => {
      const headers: any = {
        'Content-Type': 'application/json',
      };
      
      // Only add Authorization header if not in cookie mode
      if (!isCookieMode && token) {
        headers.Authorization = `Bearer ${token}`;
      }
      
      const response = await fetch(url, {
        headers,
        credentials: isCookieMode ? 'include' : 'omit',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch business settings');
      }

      return response.json();
    }
  );

  const updateSettings = async (updates: Partial<BusinessSettings>) => {
    if (!currentBusiness) return;
    
    if (!isCookieMode && !token) {
      throw new Error('Authentication required');
    }

    // Merge updates with current settings to prevent other fields from being reset
    const mergedUpdates = { ...data, ...updates };

    const headers: any = {
      'Content-Type': 'application/json',
    };
    
    // Only add Authorization header if not in cookie mode
    if (!isCookieMode && token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE_URL}/businesses/${currentBusiness.id}/settings`, {
      method: 'PUT',
      headers,
      credentials: isCookieMode ? 'include' : 'omit',
      body: JSON.stringify(mergedUpdates),
    });

    if (!response.ok) {
      throw new Error('Failed to update business settings');
    }

    await mutate(
        prev => ({ ...prev!, ...updates }),
        { revalidate: false }
      );
  };

  return {
    settings: data,
    isLoading: !error && !data,
    isError: error,
    updateSettings,
    mutate,
  };
}
